import React, {useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {Helmet} from "react-helmet";
import {
    useSpanishLeagueEventsData,
    useStremioChannelsData
} from "../../hooks/useCatalogRowData";
import useLiveChannelDetailsData, {
    isLeagueGameStarted,
    leagueGameStartsToday
} from "../../hooks/useLiveChannelDetailsData";
import {getLocalDateTimeString} from "../../../../utils/utils";
import {LeagueEvent} from "../../../../apis/towers-sports-api/types";
import {Spinner} from "../../../shared";
import CachedImage from "../../../shared/cached-image/cached-image";
import TvMediaCard from "../components/TvMediaCard";
import TvMediaRow from "../components/TvMediaRow";
import TvModal from "../components/TvModal";

export const SquadflixTvLiveHub: React.FC = () => {
    const navigate = useNavigate();
    const {events, loading: loadingEvents} = useSpanishLeagueEventsData();
    const {channels, loading: loadingChannels} = useStremioChannelsData();
    const [activeSection, setActiveSection] = useState<"all" | "sports" | "channels">("all");
    const [channelFilter, setChannelFilter] = useState("");
    const [selectedEvent, setSelectedEvent] = useState<LeagueEvent | null>(null);

    const filteredChannels = channels.filter((ch) =>
        (ch.name || "").toLowerCase().includes(channelFilter.toLowerCase())
    );

    return (
        <div className="tv-live-screen">
            <Helmet>
                <meta charSet="utf-8" />
                <title>En Vivo | SquadFlix TV</title>
            </Helmet>

            {/* Header & Section Pills */}
            <header className="tv-live-screen__header" data-tv-row="tv-live-header">
                <div>
                    <span className="tv-pill tv-pill--accent mb-2">
                        <i className="fa-solid fa-tower-broadcast pe-2"></i>SquadFlix Live TV
                    </span>
                    <h1 className="tv-live-screen__title">Deportes y Canales en Directo</h1>
                </div>

                <div className="tv-live-screen__tabs">
                    <button
                        type="button"
                        data-tv-focusable="true"
                        data-tv-autofocus="true"
                        className={`tv-pill-btn ${activeSection === "all" ? "is-active" : ""}`}
                        onClick={() => setActiveSection("all")}>
                        Todo en Directo
                    </button>
                    <button
                        type="button"
                        data-tv-focusable="true"
                        className={`tv-pill-btn ${activeSection === "sports" ? "is-active" : ""}`}
                        onClick={() => setActiveSection("sports")}>
                        <i className="fa-solid fa-futbol pe-2"></i>Partidos ({events.length})
                    </button>
                    <button
                        type="button"
                        data-tv-focusable="true"
                        className={`tv-pill-btn ${activeSection === "channels" ? "is-active" : ""}`}
                        onClick={() => setActiveSection("channels")}>
                        <i className="fa-solid fa-tv pe-2"></i>Canales ({channels.length})
                    </button>
                    <button
                        type="button"
                        data-tv-focusable="true"
                        className="tv-btn tv-btn--glass"
                        onClick={() => navigate("/squadflix/live/custom")}>
                        <i className="fa-solid fa-link pe-2"></i>Mis Enlaces
                    </button>
                </div>
            </header>

            {/* Sports Events Section */}
            {(activeSection === "all" || activeSection === "sports") && (
                <section className="mb-5">
                    {loadingEvents ? (
                        <div className="tv-loading-center py-4">
                            <Spinner />
                        </div>
                    ) : events.length > 0 ? (
                        <TvMediaRow
                            rowId="tv-live-sports-row"
                            title="Partidos y Eventos Deportivos"
                            icon={<i className="fa-solid fa-futbol"></i>}
                            count={events.length}>
                            {events.map((item) => {
                                const started = isLeagueGameStarted(item);
                                const today = leagueGameStartsToday(item);
                                const gameDate = getLocalDateTimeString(item.strTimestamp + "Z").slice(0, -3);
                                const timeOnly = gameDate.split(" ")[1];
                                const hasLinks = !!item.acestreamEvent?.enlaces?.length;

                                return (
                                    <TvMediaCard
                                        key={item.idEvent}
                                        width="360px"
                                        aspectRatio="16/9"
                                        cachedSrc={item.strThumb}
                                        showPlayOverlay={started && hasLinks}
                                        badgeTopLeft={
                                            started ? (
                                                <span className="tv-badge-live">● EN VIVO</span>
                                            ) : today ? (
                                                `Hoy ${timeOnly}`
                                            ) : (
                                                "Próximamente"
                                            )
                                        }
                                        badgeBottomRight={
                                            hasLinks
                                                ? `${item.acestreamEvent.enlaces.length} enlaces`
                                                : "Sin enlaces"
                                        }
                                        meta={started ? "En juego" : "Programado"}
                                        title={item.strEvent}
                                        subtitle={gameDate}
                                        onClick={() => {
                                            if (!hasLinks) return;
                                            if (item.acestreamEvent.enlaces.length === 1 && started) {
                                                navigate(`/squadflix/live-event/${item.idEvent}/0`);
                                            } else {
                                                setSelectedEvent(item);
                                            }
                                        }}
                                        onSecondaryAction={
                                            hasLinks ? () => setSelectedEvent(item) : undefined
                                        }
                                        secondaryActionLabel="Ver opciones"
                                    />
                                );
                            })}
                        </TvMediaRow>
                    ) : null}
                </section>
            )}

            {/* Live Channels Grid */}
            {(activeSection === "all" || activeSection === "channels") && (
                <section className="tv-live-screen__channels">
                    <div className="tv-live-screen__channels-bar" data-tv-row="tv-live-channel-search">
                        <h2 className="tv-media-row__title">
                            <i className="fa-solid fa-satellite-dish pe-2 text-danger"></i>
                            Canales de Televisión ({filteredChannels.length})
                        </h2>
                        <div className="tv-live-screen__filter-input">
                            <i className="fa-solid fa-magnifying-glass"></i>
                            <input
                                type="text"
                                data-tv-focusable="true"
                                value={channelFilter}
                                onChange={(e) => setChannelFilter(e.target.value)}
                                placeholder="Filtrar canal por nombre..."
                            />
                            {channelFilter && (
                                <button
                                    type="button"
                                    data-tv-focusable="true"
                                    className="tv-btn tv-btn--ghost tv-btn--sm"
                                    onClick={() => setChannelFilter("")}>
                                    <i className="fa-solid fa-xmark"></i>
                                </button>
                            )}
                        </div>
                    </div>

                    {loadingChannels ? (
                        <div className="tv-loading-center py-5">
                            <Spinner />
                        </div>
                    ) : (
                        <div className="tv-live-channels-grid" data-tv-row="tv-live-channels-grid" data-tv-grid="true">
                            {filteredChannels.map((channel) => (
                                <TvMediaCard
                                    key={channel.id}
                                    width="100%"
                                    aspectRatio="1/1"
                                    cachedSrc={channel.poster}
                                    badgeTopRight={
                                        channel.streams ? `${channel.streams} enlaces` : "Sin enlaces"
                                    }
                                    meta="Canal en Vivo"
                                    title={channel.name}
                                    onClick={() => navigate(`/squadflix/live/channel/${channel.id}`)}
                                />
                            ))}
                        </div>
                    )}
                </section>
            )}

            <TvModal
                open={!!selectedEvent}
                onClose={() => setSelectedEvent(null)}
                title={selectedEvent?.strEvent}
                subtitle={`Elige el enlace de transmisión (${selectedEvent?.acestreamEvent?.enlaces?.length || 0} disponibles)`}>
                <div className="tv-modal-list">
                    {selectedEvent?.acestreamEvent?.enlaces?.map((link, idx) => (
                        <button
                            key={idx}
                            type="button"
                            data-tv-focusable="true"
                            data-tv-autofocus={idx === 0 ? "true" : undefined}
                            className="tv-modal-option-btn"
                            onClick={() => {
                                const evId = selectedEvent.idEvent;
                                setSelectedEvent(null);
                                navigate(`/squadflix/live-event/${evId}/${idx}`);
                            }}>
                            <span>
                                <i className="fa-solid fa-circle-play pe-3 text-danger"></i>
                                {link.name || `Señal ${idx + 1}`}
                            </span>
                            <i className="fa-solid fa-chevron-right"></i>
                        </button>
                    ))}
                </div>
            </TvModal>
        </div>
    );
};

export const SquadflixTvLiveChannelDetails: React.FC = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const {
        channel,
        filteredStreams,
        providers,
        search,
        setSearch,
        selectedProvider,
        setSelectedProvider,
        channelViewers,
        getStreamViewers,
        loading
    } = useLiveChannelDetailsData(id!);

    const {channels: otherChannels} = useStremioChannelsData();

    if (loading) {
        return (
            <div className="tv-loading-center">
                <Spinner />
            </div>
        );
    }

    if (!channel) {
        return (
            <div className="tv-empty-state">
                <h3>Canal no encontrado</h3>
                <button
                    type="button"
                    data-tv-focusable="true"
                    className="tv-btn tv-btn--primary mt-3"
                    onClick={() => navigate("/squadflix/live")}>
                    Volver a En Vivo
                </button>
            </div>
        );
    }

    return (
        <div className="tv-details-screen">
            <Helmet>
                <meta charSet="utf-8" />
                <title>{`${channel.name} | SquadFlix Live TV`}</title>
            </Helmet>

            <div className="tv-details-screen__backdrop">
                {channel.background && (
                    <CachedImage
                        src={channel.background}
                        className="tv-details-screen__backdrop-img"
                    />
                )}
                <div className="tv-details-screen__vignette" />
            </div>

            <div className="tv-details-screen__body">
                <div className="tv-details-screen__topbar" data-tv-row="tv-channel-topbar">
                    <button
                        type="button"
                        data-tv-focusable="true"
                        className="tv-btn tv-btn--glass"
                        onClick={() => navigate("/squadflix/live")}>
                        <i className="fa-solid fa-arrow-left pe-2"></i>Volver a Canales en Vivo
                    </button>
                </div>

                <div className="tv-details-screen__hero">
                    <div className="tv-details-screen__meta-row">
                        <span className="tv-pill tv-pill--accent">
                            <i className="fa-solid fa-tower-broadcast pe-2"></i>Canal en Directo
                        </span>
                        <span className="tv-pill">{filteredStreams.length} señales disponibles</span>
                        <span className="tv-pill">
                            <i className="fa-solid fa-eye pe-2 text-success"></i>
                            {channelViewers} viendo ahora
                        </span>
                    </div>
                    <h1 className="tv-details-screen__title">{channel.name}</h1>
                </div>

                {/* Provider Filter Pills & Search */}
                <div className="tv-season-pills mb-4" data-tv-row="tv-channel-providers">
                    {providers.map((prov) => (
                        <button
                            key={prov.value || "all"}
                            type="button"
                            data-tv-focusable="true"
                            className={`tv-pill-btn ${selectedProvider === prov.value ? "is-active" : ""}`}
                            onClick={() => setSelectedProvider(prov.value)}>
                            {prov.label}
                        </button>
                    ))}
                    <div className="tv-live-screen__filter-input ms-auto">
                        <i className="fa-solid fa-magnifying-glass"></i>
                        <input
                            type="text"
                            data-tv-focusable="true"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Buscar señal..."
                        />
                    </div>
                </div>

                {/* Streams Grid */}
                <div className="tv-streams-grid" data-tv-row="tv-channel-streams">
                    {filteredStreams.map((stream, index) => {
                        const viewers = getStreamViewers(stream.hash);
                        const providerTitle = stream.name?.split("\n")[0] || `Señal ${index + 1}`;
                        return (
                            <button
                                key={stream.hash || index}
                                type="button"
                                data-tv-focusable="true"
                                data-tv-autofocus={index === 0 ? "true" : undefined}
                                className="tv-stream-card"
                                onClick={() =>
                                    navigate(`/squadflix/live/channel/${id}/stream/${stream.hash}`)
                                }>
                                <div className="tv-stream-card__icon">
                                    <i className="fa-solid fa-play"></i>
                                </div>
                                <div className="tv-stream-card__body">
                                    <div className="tv-stream-card__provider">{providerTitle}</div>
                                    <div className="tv-stream-card__desc">{stream.description}</div>
                                    <div className="tv-stream-card__hash">ID: {stream.hash?.slice(0, 14)}...</div>
                                </div>
                                <div className="tv-stream-card__viewers">
                                    <i className="fa-solid fa-user-group pe-1"></i>
                                    {viewers}
                                </div>
                            </button>
                        );
                    })}
                </div>

                {/* Other Channels Row */}
                {otherChannels.length > 1 && (
                    <div className="mt-5">
                        <TvMediaRow
                            rowId="tv-other-channels-row"
                            title="Otros Canales en Directo"
                            icon={<i className="fa-solid fa-tv"></i>}>
                            {otherChannels
                                .filter((c) => c.id !== id)
                                .map((c) => (
                                    <TvMediaCard
                                        key={c.id}
                                        width="195px"
                                        aspectRatio="1/1"
                                        cachedSrc={c.poster}
                                        badgeTopRight={c.streams ? `${c.streams} enlaces` : undefined}
                                        title={c.name}
                                        onClick={() => navigate(`/squadflix/live/channel/${c.id}`)}
                                    />
                                ))}
                        </TvMediaRow>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SquadflixTvLiveHub;
